import { X, Star, Archive, Trash2, Mail, Calendar, User, Sparkles, MessageCircle, CheckCircle, Package } from 'lucide-react';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useEmailBody, useUpdateEmail } from '@/hooks/useEmails';
import { useAnalyzeEmail } from '@/hooks/useSecretary';
import { usePluginData } from '@/hooks/usePlugins';
import AnnotationsList from './AnnotationsList';
import EventLinks from './EventLinks';
import PluginDataDisplay from './PluginDataDisplay';
import SafeEmailBody from '@/components/common/SafeEmailBody';
import { formatDate, formatEmailAddress, getCategoryColor, formatCategory, getPriorityColor, formatPriority } from '@/utils/formatters';

export default function EmailDetail({ email, onClose, onStar, onArchive, onDelete, onThreadClick, onEventClick }) {
  const { data: emailWithBody, isLoading } = useEmailBody(email?.id);
  const { data: pluginData = [] } = usePluginData(email?.id);
  const updateEmail = useUpdateEmail();
  const analyzeEmail = useAnalyzeEmail();

  if (!email) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Select an email to view
      </div>
    );
  }

  const fullEmail = emailWithBody || email;

  // Merge fresh API data into display email for up-to-date fields (summary, priority, etc.)
  const displayEmail = emailWithBody ? { ...email, ...emailWithBody } : email;

  const handleAnalyze = async () => {
    try {
      await analyzeEmail.mutateAsync(email.id);
    } catch (err) {
      console.error('Failed to analyze email:', err);
    }
  };

  const handleMarkReplied = async () => {
    try {
      await updateEmail.mutateAsync({
        id: email.id,
        data: { reply_status: 'replied' }
      });
    } catch (err) {
      console.error('Failed to update reply status:', err);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold truncate flex-1">Email Detail</h2>
        <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center gap-2 p-3 sm:p-4 border-b bg-muted/50 flex-wrap">
          <Button
            variant={displayEmail.starred ? 'default' : 'outline'}
            size="sm"
            onClick={() => onStar?.(email)}
            className="flex items-center gap-1 sm:gap-2"
          >
            <Star className={`h-4 w-4 ${displayEmail.starred ? 'fill-current' : ''}`} />
          <span className="hidden sm:inline">{displayEmail.starred ? 'Starred' : 'Star'}</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onArchive?.(email)}
          className="flex items-center gap-1 sm:gap-2"
        >
          <Archive className="h-4 w-4" />
          <span className="hidden sm:inline">Archive</span>
        </Button>

        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            if (confirm('Delete this email?')) {
              onDelete?.(email);
              onClose();
            }
          }}
          className="flex items-center gap-1 sm:gap-2"
        >
          <Trash2 className="h-4 w-4" />
          <span className="hidden sm:inline">Delete</span>
        </Button>

        <div className="flex-1" />

        {/* AI Analysis */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleAnalyze}
          disabled={analyzeEmail.isPending}
          className="flex items-center gap-1 sm:gap-2"
        >
          <Sparkles className="h-4 w-4" />
          <span className="hidden sm:inline">{analyzeEmail.isPending ? 'Analyzing...' : 'Analyze'}</span>
        </Button>

        {/* Thread Link */}
          {displayEmail.thread_id && onThreadClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onThreadClick(displayEmail.thread_id)}
            className="flex items-center gap-1 sm:gap-2"
          >
            <MessageCircle className="h-4 w-4" />
          <span className="hidden sm:inline">Thread</span>
            </Button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Subject */}
          <h1 className="text-xl sm:text-2xl font-bold mb-4 break-words">
            {displayEmail.subject || '(No subject)'}
          </h1>

          {/* Badges */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {displayEmail.category && (
              <Badge className={getCategoryColor(displayEmail.category)}>
                {formatCategory(displayEmail.category)}
              </Badge>
            )}
            {displayEmail.priority && (
              <Badge className={getPriorityColor(displayEmail.priority)}>
                {formatPriority(displayEmail.priority)}
              </Badge>
            )}
            {displayEmail.action_needed && (
              <Badge variant="warning">Action Needed</Badge>
            )}
            {displayEmail.reply_status === 'awaiting' && (
              <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200">
                Awaiting Reply
              </Badge>
            )}
            {displayEmail.reply_status === 'replied' && (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200">
                Replied
              </Badge>
            )}
          </div>

          {/* Reply Status Section */}
          {displayEmail.reply_status === 'awaiting' && (
            <div className="mb-6 p-4 rounded-lg border bg-orange-50 dark:bg-orange-950/20">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium mb-1 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Reply Needed
                  </h3>
                  {displayEmail.reply_by_date && (
                    <p className="text-sm text-muted-foreground">
                      Reply by: {formatDate(displayEmail.reply_by_date)}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMarkReplied}
                  disabled={updateEmail.isPending}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Mark as Replied
                </Button>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="space-y-3 mb-6 p-3 sm:p-4 rounded-lg bg-muted">
            <div className="flex items-start gap-3">
              <User className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">From</p>
                <p className="text-sm text-muted-foreground break-words">{displayEmail.from_address}</p>
              </div>
            </div>

            {displayEmail.to_address && (
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">To</p>
                  <p className="text-sm text-muted-foreground break-words">{displayEmail.to_address}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Date</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(displayEmail.date)}
                </p>
              </div>
            </div>
          </div>

          {/* AI Summary */}
          {displayEmail.summary && (
            <div className="mb-6 p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/20">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <span className="text-sm">✨ AI Summary</span>
              </h3>
              <p className="text-sm">{displayEmail.summary}</p>
            </div>
          )}

          {/* Events */}
          <div className="mb-6">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Related Events
            </h3>
            <EventLinks emailId={displayEmail.id} onEventClick={onEventClick} />
          </div>

          {/* Annotations */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Annotations</h3>
            <AnnotationsList emailId={displayEmail.id} />
          </div>

          {/* Plugin Data */}
          {pluginData.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Plugin Analysis
              </h3>
              <PluginDataDisplay pluginData={pluginData} />
            </div>
          )}

        {/* Email Body */}
        <div className="border-t pt-6">
          {isLoading ? (
            <div className="py-12">
              <LoadingSpinner />
              <p className="text-center text-sm text-muted-foreground mt-4">
                Loading email body...
              </p>
            </div>
          ) : fullEmail.body?.html ? (
            <SafeEmailBody html={fullEmail.body.html} className="prose prose-sm max-w-none dark:prose-invert break-words overflow-x-auto" />
          ) : fullEmail.body?.text ? (
            <pre className="whitespace-pre-wrap text-sm font-sans break-words overflow-x-auto">
              {fullEmail.body.text}
            </pre>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No email body available
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
